export type OutgoingIntakeStatus =
  | "draft"
  | "sent"
  | "in_progress"
  | "awaiting_review"
  | "revision_requested"
  | "accepted"
  | "expired";

export type OutgoingIntakeQuestionType =
  | "short_text"
  | "long_text"
  | "boolean"
  | "date"
  | "single_select"
  | "multi_select"
  | "file"
  | "address";

export type OutgoingIntakeFormNodeType =
  | "section"
  | "question"
  | "repeatable_group";

export type OutgoingIntakeOption = {
  option_id: string;
  label: string;
};

/**
 * Show a step only when an earlier answer matches: an option id for single
 * select, any of a list for multi select, a boolean for yes/no, else text.
 */
export type OutgoingIntakeShowIf = {
  question_id: string;
  equals: string | boolean | string[];
};

export type OutgoingIntakeFormNode = {
  node_id: string;
  node_type: OutgoingIntakeFormNodeType;
  title?: string | null;

  prompt?: string | null;
  question_type?: OutgoingIntakeQuestionType | null;
  required?: boolean;
  help_text?: string | null;
  options?: OutgoingIntakeOption[];
  model_path?: string | null;
  show_if?: OutgoingIntakeShowIf | null;
  template_source?: string | null;
  order?: number;

  uscis_form_number?: string | null;
  uscis_schema_field?: string | null;
  extend_field_type?: string | null;
  extend_bbox?: Record<string, unknown> | null;
  extend_bboxes?: Record<string, unknown>[] | null;
  extend_page_index?: number | null;
  extend_text_edit_options?: Record<string, unknown> | null;

  children?: OutgoingIntakeFormNode[];
  min_items?: number | null;
  max_items?: number | null;
};

/** Optional conversation context that helps name an intake. */
export type IntakeTitleContextMessage = {
  role: "user" | "assistant" | "system";
  content: string;
};

export type GenerateIntakeTitleRequest = {
  schema: OutgoingIntakeFormNode[];
  messages?: IntakeTitleContextMessage[];
};

export type GenerateIntakeTitleResponse = {
  title: string;
};

export type OutgoingIntakeEventKind =
  | "sent"
  | "resent"
  | "delivered"
  | "opened"
  | "reminded"
  | "bounced"
  | "revision_requested"
  | "submitted"
  | "extended"
  | "revoked"
  | "cancelled"
  | "link_requested"
  | "accepted"
  | "filed";

export type OutgoingIntakeEvent = {
  kind: OutgoingIntakeEventKind;
  at: unknown;
  actor: "attorney" | "client" | "system";
  detail?: Record<string, unknown> | null;
};

/** Automatic reminders: days after sending, and days before the link expires. */
export type OutgoingIntakeReminderSchedule = {
  days_after_send: number[];
  days_before_expiry?: number | null;
};

export type OutgoingIntake = {
  outgoing_intake_id: string;
  foreign_national_id: string;
  practice_name: string;
  law_firm_id?: string | null;
  created_by_user_id: string;
  status: OutgoingIntakeStatus;
  schema: OutgoingIntakeFormNode[];
  title: string;
  recipient_email?: string | null;
  attorney_note?: string | null;
  welcome_video_url?: string | null;
  welcome_video_storage_path?: string | null;
  /** Set when the video is a library entry (a template's, a saved one). */
  welcome_video_id?: string | null;
  welcome_video_trim?: WelcomeVideoTrim | null;
  enable_firm_branding?: boolean | null;
  intake_background_color?: string | null;
  sent_at?: unknown;
  expires_at?: unknown;
  /** Only present on the response to the send that created it. */
  public_token?: string | null;
  /** A link can be fetched with getLink; the link itself is never in this payload. */
  has_public_link?: boolean | null;
  latest_submission_id?: string | null;
  progress_answers?: OutgoingIntakeAnswer[] | null;
  progress_saved_at?: unknown;
  law_firm_logo_url?: string | null;
  enable_whitelabel_foreign_national_intake?: boolean | null;
  completed_at?: unknown;
  created_at?: unknown;
  updated_at?: unknown;
  /** Saved on the draft; the send defaults to it. */
  expires_in_days?: number | null;
  reminder_schedule?: OutgoingIntakeReminderSchedule | null;
  events?: OutgoingIntakeEvent[] | null;
  opened_at?: unknown;
  last_opened_at?: unknown;
  last_reminded_at?: unknown;
  reminder_count?: number | null;
  email_delivered_at?: unknown;
  email_opened_at?: unknown;
  email_bounced_at?: unknown;
  email_bounce_reason?: string | null;
  link_revoked_at?: unknown;
  cancelled_at?: unknown;
  /** The template it was built from (a saved template id or a catalog id). */
  source_template_id?: string | null;
};

/** A field an answer can be written to when the intake is accepted. */
export type OutgoingIntakeSaveTarget = {
  model_path: string;
  label: string;
  group: string;
  question_types: OutgoingIntakeQuestionType[];
};

export type OutgoingIntakePresendIssue = {
  severity: "error" | "warning";
  code: string;
  node_id: string | null;
  message: string;
};

/** Client-visible text that reads like it was meant for the firm. */
export type OutgoingIntakeLintIssue = {
  /** "title", "note", "{node_id}.prompt|title|help" or "{node_id}.opt.{option_id}". */
  key: string;
  node_id: string | null;
  field: "title" | "prompt" | "help" | "note" | "opt";
  /** "helper text", "question", "answer choice", … */
  where: string;
  text: string;
  match: string | null;
  reason: string;
  /** A client-facing rewrite; "" means drop the text; null when none. */
  suggestion: string | null;
};

export type OutgoingIntakeLintResult = {
  issues: OutgoingIntakeLintIssue[];
  model_checked: boolean;
};

/** One answer beside what the record holds today (`GET .../view`). */
export type OutgoingIntakeSubmissionViewAnswer = {
  answer_key: string;
  question?: string | null;
  question_type?: string | null;
  value?: unknown;
  files?: OutgoingIntakeFile[];
  maps_to?: string | null;
  record_value?: unknown;
  comparison?: "matches" | "changes" | "new" | null;
  decision?: "accepted" | "rejected" | "pending" | null;
};

export type OutgoingIntakeSubmissionView = {
  submission_id: string;
  revision_number?: number;
  answers: OutgoingIntakeSubmissionViewAnswer[];
};

/** A profile field for an answer that isn't saved anywhere (`POST .../suggest_targets`). */
export type OutgoingIntakeTargetSuggestion = {
  answer_key: string;
  question_id: string;
  model_path: string;
  label: string;
};

export type OutgoingIntakeTargetSuggestions = {
  suggestions: OutgoingIntakeTargetSuggestion[];
  model_checked: boolean;
};

export type OutgoingIntakeDuplicateMatch = {
  id: string;
  source?: "staging" | "evidence" | "outgoing_intake" | string;
  docNames?: string;
};

export type OutgoingIntakeFile = {
  file_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  storage_path: string;
  download_url: string;
  pdfFilePath?: string | null;
  previewStatus?: "pending" | "ready" | "failed" | "skipped" | string | null;
  previewError?: string | null;
  sha256?: string | null;
  reviewStatus?:
    | "checking"
    | "passed"
    | "warning"
    | "failed"
    | "skipped"
    | string
    | null;
  reviewWarnings?: Array<{
    type: "file_integrity" | "readability" | "duplicate" | string;
    message: string;
    details?: unknown;
  }>;
  fileIntegrity?: {
    status?: string;
    message?: string;
  } | null;
  readability?: {
    status?: string;
    message?: string;
    details?: unknown;
  } | null;
  duplicate?: {
    status?: string;
    message?: string;
    matches?: OutgoingIntakeDuplicateMatch[];
  } | null;
  uploaded_at?: unknown;
};

/** Where an answer's value came from. Absent on answers predating Ask Casey. */
export type OutgoingIntakeAnswerSource = "client" | "assistant";

export type OutgoingIntakeAnswerSourceMetadata = {
  /** How the assistant arrived at the value, e.g. "document" or "conversation". */
  origin?: string | null;
  /** Filename of the document the value was read from, when there was one. */
  document_filename?: string | null;
  /** The text on the page that supports the value, for the reviewer to check. */
  reference_text?: string | null;
  page?: number | null;
  confidence?: number | null;
  accepted_at?: unknown;
};

export type OutgoingIntakeAnswer = {
  question_id: string;
  repeatable_group_id?: string | null;
  repeatable_group_instance_id?: string | null;
  value?: unknown;
  files?: OutgoingIntakeFile[];
  comment?: string | null;
  source?: OutgoingIntakeAnswerSource | null;
  source_metadata?: OutgoingIntakeAnswerSourceMetadata | null;
};

export type OutgoingIntakeReviewDecisionStatus = "accepted" | "rejected";

export type OutgoingIntakeReviewDecision = {
  question_id: string;
  repeatable_group_id?: string | null;
  repeatable_group_instance_id?: string | null;
  status: OutgoingIntakeReviewDecisionStatus;
  note?: string | null;
};

export type OutgoingIntakeSubmissionReviewStatus =
  | "awaiting_review"
  | "revision_requested"
  | "accepted";

export type OutgoingIntakeSubmission = {
  outgoing_intake_submission_id: string;
  outgoing_intake_id: string;
  foreign_national_id: string;
  revision_number: number;
  previous_submission_id?: string | null;
  submitted_at?: unknown;
  answers: OutgoingIntakeAnswer[];
  review_status: OutgoingIntakeSubmissionReviewStatus;
  reviewed_at?: unknown;
  reviewed_by_user_id?: string | null;
  decisions: OutgoingIntakeReviewDecision[];
  revision_request_note?: string | null;
  created_at?: unknown;
  updated_at?: unknown;
};

export type OutgoingIntakeTemplateCategory = "building_block" | "visa" | "form";

export type OutgoingIntakeTemplate = {
  template_id: string;
  label: string;
  /**
   * Absent until the template's schema has been fetched — the catalog listing
   * carries metadata only. Use `ensureTemplateSchemas` before reading it.
   */
  schema?: OutgoingIntakeFormNode[];
  /** Counts for the chooser's cards, always present so cards need no schema. */
  question_count?: number;
  document_count?: number;
  /** Broad grouping used by the template chooser (visa starter, govt form, etc.). */
  category?: OutgoingIntakeTemplateCategory;
  /** Government agency for `form` templates (USCIS/DOL/EOIR/DOS). */
  agency?: string;
  /** Visa type value for `visa` starter templates (e.g. "EB-1A"). */
  visa?: string;
  /** Practice-area grouping for `visa` starters (e.g. "Employment-based"). */
  visa_category?: string;
  /** Short human description shown in the chooser. */
  description?: string;
};

export type OutgoingIntakeSavedTemplate = {
  template_id: string;
  law_firm_id: string;
  name: string;
  schema: OutgoingIntakeFormNode[];
  created_by_user_id: string;
  source_outgoing_intake_id?: string | null;
  /** The visa it is for; null or absent reads as Any. */
  visa?: string | null;
  /** One line on what it covers. */
  description?: string | null;
  /** Set while archived: out of every picker, restorable. */
  archived_at?: unknown;
  updated_by_user_id?: string | null;
  /** Intakes sent from it. */
  sent_count?: number;
  last_sent_at?: unknown;
  created_at?: unknown;
  updated_at?: unknown;
} & OutgoingIntakeTemplateDefaults & {
    welcome_video_id?: string | null;
    welcome_video_url?: string | null;
    welcome_video_storage_path?: string | null;
    welcome_video_trim?: WelcomeVideoTrim | null;
  };

/** The part of a welcome video that plays, in seconds; the file is uncut. */
export type WelcomeVideoTrim = { start: number; end?: number | null };

/** A video in the welcome-video library: the attorney's own or the firm's. */
export type WelcomeVideo = {
  video_id: string;
  law_firm_id: string;
  owner_user_id: string;
  scope: "user" | "firm";
  title: string;
  url: string;
  storage_path: string;
  duration_seconds?: number | null;
  trim?: WelcomeVideoTrim | null;
  used_count?: number;
  created_at?: unknown;
};

/** What a recorded or uploaded take carries besides the file. */
export type WelcomeVideoUploadMeta = {
  durationSeconds?: number | null;
  trim?: WelcomeVideoTrim | null;
  /** Also keep it in My Videos (intakes only). */
  saveToMyVideos?: boolean;
};

/** Stands for the client's first name in a template's note. */
export const TEMPLATE_FIRST_NAME_TOKEN = "{{first_name}}";

/** What every intake made from a template starts with; null clears one. */
export type OutgoingIntakeTemplateDefaults = {
  /** May hold `TEMPLATE_FIRST_NAME_TOKEN`. */
  attorney_note?: string | null;
  expires_in_days?: number | null;
  reminder_schedule?: OutgoingIntakeReminderSchedule | null;
  enable_firm_branding?: boolean | null;
};

/** `POST /outgoing_intakes/saved_templates/<id>/send`. */
export type SendSavedTemplateRequest = {
  foreign_national_id: string;
  email?: string | null;
  expires_days?: number;
  /** null sends without automatic reminders. */
  reminders?: OutgoingIntakeReminderSchedule | null;
  note?: string;
  title?: string;
  /** false creates the draft in the client's Requests without sending. */
  send?: boolean;
};

export type SendSavedTemplateResponse = {
  outgoing_intake: OutgoingIntake;
  sent: boolean;
  public_link?: string;
  email_delivered?: boolean;
};
